import * as React from "react";
import { ShareDialog } from "@/components/share/ShareDialog";
import type { ShareAccess, ShareRole } from "@/components/share/types";
import {
  BusinessReportOutcomeDetails,
  BusinessReportOutcomePermission,
} from "@/modules/snapshots/businessReports.types";
import {
  useCreateBusinessReportOutcomePermissionMutation,
  useDeleteBusinessReportOutcomePermissionMutation,
  useUpdateBusinessReportOutcomeAccessMutation,
} from "@/modules/snapshots/businessReports.api";
import { useSnapshotPermission } from "@/modules/snapshots/hooks/useSnapshotPermission";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";

interface Props {
  businessReportOutcome: BusinessReportOutcomeDetails;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
}

export const BusinessReportOutcomeShareModal: React.FC<Props> = ({ isOpen, setIsOpen, businessReportOutcome }) => {
  const isReadOnly = !useSnapshotPermission(businessReportOutcome);
  const authenticatedUser = useAuthenticatedUser();
  const [createPermission] = useCreateBusinessReportOutcomePermissionMutation();
  const [deletePermission] = useDeleteBusinessReportOutcomePermissionMutation();
  const [updateAccess] = useUpdateBusinessReportOutcomeAccessMutation();
  const { id } = businessReportOutcome;

  const handleShare = React.useCallback(
    async (users: string[], role: ShareRole) => {
      await createPermission({ id, role, users }).unwrap();
    },
    [createPermission, id],
  );

  const handleRemovePermission = React.useCallback(
    async (permission: BusinessReportOutcomePermission) => {
      await deletePermission({ id: permission.business_report_outcome_id, permissionId: permission.id }).unwrap();
    },
    [deletePermission],
  );

  const handleAccessChange = React.useCallback(
    async (access: ShareAccess) => {
      await updateAccess({ id, access }).unwrap();
    },
    [updateAccess, id],
  );

  return (
    <ShareDialog
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={<>Share Snapshot &#34;{businessReportOutcome.business_report.name}&#34;</>}
      isReadOnly={isReadOnly}
      permissions={businessReportOutcome.permissions}
      access={businessReportOutcome.access}
      shareSuccessMessage="Snapshot shared successfully."
      onShare={handleShare}
      onRemovePermission={handleRemovePermission}
      canRemovePermission={permission => permission.user_id !== authenticatedUser.id}
      onAccessChange={handleAccessChange}
    />
  );
};
